import {useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import {c, mono, primaryButtonSx} from "../landingTokens";
import {getDefaultSipAudioConfig, isSipConfigured, useSipAudioClient} from "../../../utils/sipAudio";
import {CallOrb, VoiceOrbGlow} from "../../../components/voice-orb";

// The extension each language's demo call dials — selected via the MK/EN toggle below.
const LANGUAGE_OPTIONS = [
  {code: "en", flag: "GB", extension: "930196", label: "English"},
  {code: "mk", flag: "mk", extension: "930195", label: "Macedonian"},
];

const LanguageToggle = ({value, onChange, disabled}) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    disabled={disabled}
    onChange={(e, next) => next && onChange(next)}
    aria-label="Demo call language"
    sx={{
      borderRadius: "980px",
      p: "3px",
      bgcolor: "rgba(20,21,26,0.04)",
      "& .MuiToggleButton-root": {
        border: "none",
        borderRadius: "980px !important",
        px: 1.3,
        py: 0.45,
        gap: 0.55,
        textTransform: "none",
        fontSize: 12,
        fontWeight: 650,
        color: c.inkMuted,
        transition: "background-color .2s ease, color .2s ease",
      },
      "& .Mui-selected": {
        bgcolor: `${c.white} !important`,
        color: `${c.purpleDeep} !important`,
        boxShadow: "0 2px 8px rgba(20,21,26,0.08)",
      },
    }}
  >
    {LANGUAGE_OPTIONS.map((opt) => (
      <ToggleButton key={opt.code} value={opt.code} aria-label={opt.label}>
        <Box component="pre" sx={{
          m: 0,
          fontFamily: mono,
          fontSize: 9.5,
          letterSpacing: "0.02em",
          color: "inherit",
          opacity: 0.65,
          px: 0.55,
          py: "1px",
          borderRadius: "4px",
          bgcolor: "rgba(20,21,26,0.06)",
        }}>
          {opt.flag}
        </Box>
        {opt.code.toUpperCase()}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export const LiveCallDemo = ({size = 240, hero = false}) => {
  const config = useMemo(() => getDefaultSipAudioConfig(), []);
  const sip = useSipAudioClient(config);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const configured = isSipConfigured(config);

  const handleStart = async () => {
    setOpen(true);
    try {
      const extension = LANGUAGE_OPTIONS.find((opt) => opt.code === lang)?.extension;
      await sip.call(extension);
    } catch {
      // sip.error already carries the message; CallOrb surfaces it.
    }
  };

  const handleClose = () => setOpen(false);

  // Keep a live reference to handleStart so the window-event listener below always invokes the
  // current sip.call closure (sip is recreated on every status change — a captured one goes stale).
  const startRef = useRef(handleStart);
  startRef.current = handleStart;

  // Lets other parts of the page open the live-call console (e.g. the header's "Test it now"
  // button) via openLiveCall(). When SIP isn't configured there's nothing to dial, so we just
  // bring the hero orb — with its "coming soon" state — into view instead.
  useEffect(() => {
    const handleOpen = () => {
      if (configured) {
        startRef.current();
      } else {
        document.getElementById("live")?.scrollIntoView({behavior: "smooth", block: "start"});
      }
    };
    window.addEventListener("open-live-call", handleOpen);
    return () => window.removeEventListener("open-live-call", handleOpen);
  }, [configured]);

  return (
    <Stack className="live-call-demo" alignItems="center" gap={hero ? 2.75 : 0} sx={{position: "relative", zIndex: 1}}>
      <Tooltip title={configured ? "" : "Ask your team to switch on REACT_APP_SIP_* to enable this demo"}>
        {/* Bare Box gives Tooltip a ref-forwarding child and anchors the ring pulses —
            VoiceOrbGlow owns its own click/keyboard handling via `onStart`. */}
        <Box sx={{position: "relative", width: size, height: size, display: "grid", placeItems: "center"}}>
          {/* Soft breathing halo — lifts the orb off the white card so it reads as a glowing,
              live presence rather than a dark sphere. */}
          {hero && (
            <Box aria-hidden className="hero-orb-halo" sx={{
              position: "absolute",
              inset: "-17%",
              borderRadius: "50%",
              pointerEvents: "none",
              filter: "blur(8px)",
              background: `radial-gradient(circle at 50% 50%, ${c.purpleMist} 0%, rgba(199,210,254,0.35) 44%, rgba(255,255,255,0) 70%)`,
            }}/>
          )}
          {hero && configured && [0, 1, 2].map((i) => (
            <Box
              key={i}
              aria-hidden
              className="hero-orb-ring"
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: `1.5px solid ${c.purple}`,
                animationDelay: `${i * 1.05}s`,
                pointerEvents: "none",
              }}
            />
          ))}
          <VoiceOrbGlow
            state="idle"
            active={open}
            size={size}
            backgroundColor={c.white}
            onStart={configured ? handleStart : undefined}
          />
        </Box>
      </Tooltip>

      {hero && (
        <Stack alignItems="center" gap={1.1} sx={{maxWidth: 330}}>
          <LanguageToggle value={lang} onChange={setLang} disabled={open} />
          <Typography sx={{fontSize: {xs: 19, md: 22}, fontWeight: 650, letterSpacing: "-0.02em", color: c.ink}}>
            Talk to OpenBrain Assistant
          </Typography>
          <Typography sx={{fontSize: 13.5, lineHeight: 1.55, color: c.inkSoft, textAlign: "center"}}>
            OpenBrain's own voice assistant — ask anything about the platform, or book a demo,
            live over a call.
          </Typography>
          <Button
            variant={"contained"} size={"large"} color={"primary"}
            disabled={!configured || open}
            onClick={handleStart}
            sx={{
              ...primaryButtonSx,
              minWidth: 250,
              py: 1.4,
              borderRadius: "12px",
              fontWeight: 700
            }}
          >
            Start talking
          </Button>
        </Stack>
      )}

      {/* Mounted as soon as the widget exists so sip.js has an <audio> element ready before it dials. */}
      <audio ref={sip.remoteAudioRef} hidden autoPlay playsInline />

      {open && (
        <CallOrb
          sip={sip}
          onClose={handleClose}
          extension={LANGUAGE_OPTIONS.find((opt) => opt.code === lang)?.extension}
        />
      )}
    </Stack>
  );
};
